import type { FC, ReactNode } from 'react';

import { cx } from '@/utils/cx';

const ICON_CLASSES = 'w-3.5 h-3.5 text-fg-quaternary flex-shrink-0 mt-0.5';
const LABEL_CLASSES =
  'text-xs md:text-sm text-fg-quaternary w-28 flex-shrink-0';

interface OverviewFieldRowProps {
  icon: FC<{ className?: string }>;
  label: string;
  children: ReactNode;
  /** Extra classes for the value slot (typography + wrapping). The slot always includes `min-w-0`. */
  valueClassName?: string;
  /** Extra classes for the row container (e.g. 'group' for hover-reveal affordances). */
  rowClassName?: string;
  /** Sibling rendered after the value slot (e.g. a copy button). */
  trailing?: ReactNode;
}

/**
 * Canonical overview-column row layout: icon → label → value slot.
 * Single source of truth for the `gap-2` + label `w-28` geometry so the
 * standard OverviewSection rows and the enrichment rows can never drift again
 * (see issue #9195; previously duplicated across 4 files).
 */
export function OverviewFieldRow({
  icon: Icon,
  label,
  children,
  valueClassName,
  rowClassName,
  trailing,
}: OverviewFieldRowProps) {
  return (
    <div className={cx('flex items-start gap-2', rowClassName)}>
      <Icon className={ICON_CLASSES} aria-hidden='true' />
      <span className={LABEL_CLASSES}>{label}</span>
      <div className={cx('min-w-0', valueClassName)}>{children}</div>
      {trailing}
    </div>
  );
}
